import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { IncidenceQueryData } from 'modules/bookkeepers/hooks/useGetIncidences'
import {
  ElectiveIncidenceStatus,
  InhouseIncidenceStatus,
} from 'modules/payroll_bundle/lib/Incidences/Status'

interface Props {
  onSelectNewStatus: (newStatus: ElectiveIncidenceStatus) => void
  isLoading: boolean
  selectedElements: IncidenceQueryData[]
}

const allowedStatusChanges: Record<string, InhouseIncidenceStatus[]> = {
  'to-do': ['doing', 'done', 'discarded'],
  doing: ['to-do', 'done', 'discarded'],
  done: [],
  discarded: [],
}

const availableOptions = (currentSelection: IncidenceQueryData[]) => {
  const disabledOptions: Record<InhouseIncidenceStatus, boolean> = {
    'to-do': true,
    doing: true,
    done: true,
    discarded: true,
  }
  // build up our allowed transitions
  for (const selectedIncidence of currentSelection) {
    // Update the allowed status changes
    for (const allowedChange of allowedStatusChanges[selectedIncidence.status] ?? []) {
      disabledOptions[allowedChange] = false
    }
    // Check if all are allowed, and if so break
    const someDisabled = Object.values(disabledOptions).some((disabled) => disabled)
    if (!someDisabled) {
      break
    }
  }
  return disabledOptions
}

const ActionsMenu = ({ onSelectNewStatus, isLoading, selectedElements }: Props) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  function handlePopoverToggle(newPopoverState: boolean) {
    setIsPopoverOpen(newPopoverState)
  }
  const hasInhousePayroll = hasFeature('inhouse_payroll')

  // TODO: Rework logic so that we have a simpler check for professionals status checks
  const disabledStatuses = availableOptions(selectedElements)
  const isSendToBookkeeperOptionAvailable = hasFeature('factorial_professionals')

  const actionableProfessionalsIncidencesSelected = selectedElements.some(
    (incidence) => incidence.status === 'in-preparation'
  )

  return (
    <PopoverMenu type="list" open={isPopoverOpen} onClick={handlePopoverToggle}>
      <PopoverMenu.Trigger>
        <Button type="secondary">
          {i18n.t('bookkeepers.incidence.bulk_status_changes.actions_menu.name')}
        </Button>
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="start">
        {hasInhousePayroll && (
          <>
            <PopoverMenu.TouchableOption
              title={i18n.t('bookkeepers.incidence.bulk_status_changes.actions_menu.to_do')}
              onClick={() => onSelectNewStatus('to-do')}
              disabled={isLoading || disabledStatuses['to-do']}
            />
            <PopoverMenu.TouchableOption
              title={i18n.t('bookkeepers.incidence.bulk_status_changes.actions_menu.doing')}
              onClick={() => onSelectNewStatus('doing')}
              disabled={isLoading || disabledStatuses['doing']}
            />
            <PopoverMenu.TouchableOption
              title={i18n.t('bookkeepers.incidence.bulk_status_changes.actions_menu.done')}
              onClick={() => onSelectNewStatus('done')}
              disabled={isLoading || disabledStatuses['done']}
            />
          </>
        )}

        {isSendToBookkeeperOptionAvailable && (
          <PopoverMenu.TouchableOption
            title={i18n.t(
              'bookkeepers.incidence.bulk_status_changes.actions_menu.send_to_bookkeeper'
            )}
            onClick={() => onSelectNewStatus('to-do')}
            disabled={isLoading || !actionableProfessionalsIncidencesSelected}
          />
        )}

        <PopoverMenu.TouchableOption
          danger
          title={i18n.t('bookkeepers.incidence.bulk_status_changes.actions_menu.discard')}
          onClick={() => onSelectNewStatus('discarded')}
          disabled={
            isLoading ||
            (hasInhousePayroll && disabledStatuses['discarded']) ||
            (isSendToBookkeeperOptionAvailable && !actionableProfessionalsIncidencesSelected)
          }
        />
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

export default observer(ActionsMenu)
